import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { formatDate } from "../../utils";
import logo from "../../assets/ooklass-blue-removebg.png";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const avgColor = (avg) => {
  if (avg >= 75) return "#2e7d32";
  if (avg >= 50) return "#e65100";
  return "#c62828";
};

const SubjectLineChart = ({ subject }) => {
  const sorted = [...subject.tests].sort(
    (a, b) => new Date(a.dateOfExam) - new Date(b.dateOfExam)
  );
  const labels = sorted.map((t) => formatDate(new Date(t.dateOfExam)));
  const scores = sorted.map((t) => (t.marks === -1 ? null : (t.marks * 100) / t.maxMarks));
  const avg = subject.average;

  const data = {
    labels,
    datasets: [
      {
        label: "Score %",
        data: scores,
        borderColor: "#1565c0",
        backgroundColor: "rgba(21,101,192,0.15)",
        tension: 0.3,
        pointRadius: 4,
        fill: true,
        spanGaps: true,
      },
      {
        label: `Avg ${avg.toFixed(1)}%`,
        data: labels.map(() => avg),
        borderColor: "#9e9e9e",
        borderDash: [6, 3],
        borderWidth: 1.5,
        pointRadius: 0,
        fill: false,
      },
    ],
  };

  const options = {
    responsive: true,
    animation: false,
    plugins: {
      legend: { position: "top", labels: { font: { size: 11 } } },
      title: { display: false },
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: { callback: (v) => `${v}%` },
      },
    },
  };

  return <Line data={data} options={options} />;
};

const StudentReportCard = React.forwardRef(({ report, remarks }, ref) => {
  if (!report) return null;

  return (
    <div
      ref={ref}
      id="student-report"
      style={{
        background: "#fff",
        padding: "32px 32px 48px 32px",
        fontFamily: "Arial, sans-serif",
        color: "#111",
        minWidth: "700px",
      }}
    >
      {/* ── Header + Student Info + Fees ── */}
      <div data-pdf-section="header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px",
          }}
        >
          <div>
            <img src={logo} alt="OOKlass" style={{ height: "52px", display: "block" }} />
            <div style={{ fontSize: "12px", color: "#555", marginTop: "2px" }}>
              Science &amp; Maths · Classes 5 to 12
            </div>
          </div>
          <div
            style={{
              fontSize: "15px",
              fontWeight: "600",
              background: "#e3f2fd",
              color: "#1565c0",
              borderRadius: "6px",
              padding: "6px 14px",
            }}
          >
            Batch {report.batchYear}
          </div>
        </div>

        <hr style={{ border: "none", borderTop: "2px solid #1565c0", marginBottom: "16px" }} />

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
          <tbody>
            <tr>
              <InfoCell label="Student" value={report.name} />
              <InfoCell label="Class" value={`${report.class}th`} />
            </tr>
            <tr>
              <InfoCell label="Father" value={report.fatherName} />
              <InfoCell label="School" value={report.school || "—"} />
            </tr>
            <tr>
              <InfoCell label="DOB" value={formatDate(new Date(report.dateOfBirth))} />
              <InfoCell label="Contact" value={report.parentContact || "—"} />
            </tr>
            <tr>
              <InfoCell label="Status" value={report.enrollmentStatus} />
              <td />
            </tr>
          </tbody>
        </table>

      </div>

      {/* ── Performance Summary ── */}
      <div data-pdf-section="summary">
      <SectionTitle>Performance Summary</SectionTitle>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginBottom: "24px",
          fontSize: "13px",
        }}
      >
        <thead>
          <tr style={{ background: "#1565c0", color: "#fff" }}>
            {["Subject", "Tests", "Avg %", "Highest", "Lowest", "Absent"].map((h) => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.subjects.map((s, i) => {
            const absentCount = s.tests.filter((t) => t.marks === -1 || t.marks == null).length;
            return (
              <tr key={s.courseId} style={{ background: i % 2 === 0 ? "#f5f5f5" : "#fff" }}>
                <td style={tdStyle}>{s.courseName}</td>
                <td style={tdStyle}>{s.testCount}</td>
                <td style={{ ...tdStyle, color: avgColor(s.average), fontWeight: "600" }}>
                  {s.average.toFixed(1)}%
                </td>
                <td style={tdStyle}>{s.highest}</td>
                <td style={tdStyle}>{s.lowest}</td>
                <td style={{ ...tdStyle, color: absentCount > 0 ? "#c62828" : "#757575" }}>
                  {absentCount > 0 ? absentCount : "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {/* ── Per-Subject Detail ── */}
      {report.subjects.map((subject) => (
        <div
          key={subject.courseId}
          data-pdf-section={`subject-${subject.courseId}`}
          style={{
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            padding: "16px",
            marginBottom: "20px",
          }}
        >
          <div style={{ fontSize: "15px", fontWeight: "700", marginBottom: "12px", color: "#1565c0" }}>
            {subject.courseName}
          </div>

          {/* Line chart */}
          <div style={{ maxHeight: "220px", marginBottom: "14px" }}>
            <SubjectLineChart subject={subject} />
          </div>

          {/* Test detail table */}
          {(() => {
            const sortedTests = [...subject.tests].sort(
              (a, b) => new Date(b.dateOfExam) - new Date(a.dateOfExam)
            );
            const absentTests = sortedTests.filter((t) => t.marks === -1 || t.marks == null);
            return (
              <>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <thead>
                    <tr style={{ background: "#f5f5f5" }}>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Marks</th>
                      <th style={thStyle}>Max Marks</th>
                      <th style={thStyle}>Score %</th>
                      <th style={thStyle}>Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTests.map((test, i) => {
                      const absent = test.marks === -1 || test.marks == null;
                      const pct = absent ? "—" : `${((test.marks * 100) / test.maxMarks).toFixed(1)}%`;
                      return (
                        <tr
                          key={i}
                          style={{
                            background: absent ? "#fff5f5" : i % 2 === 0 ? "#fff" : "#fafafa",
                          }}
                        >
                          <td style={tdStyle}>{formatDate(new Date(test.dateOfExam))}</td>
                          <td style={{ ...tdStyle, color: absent ? "#c62828" : undefined, fontStyle: absent ? "italic" : undefined }}>
                            {absent ? "Absent" : test.marks}
                          </td>
                          <td style={{ ...tdStyle, color: absent ? "#9e9e9e" : undefined }}>
                            {absent ? "—" : test.maxMarks}
                          </td>
                          <td style={{ ...tdStyle, color: absent ? "#9e9e9e" : avgColor(parseFloat(pct)) }}>
                            {pct}
                          </td>
                          <td style={{ ...tdStyle, color: absent ? "#9e9e9e" : "#424242" }}>
                            {absent ? "—" : (test.rankInClass && test.totalStudentsPresent ? `${test.rankInClass} / ${test.totalStudentsPresent}` : "—")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Absent badges */}
                {absentTests.length > 0 && (
                  <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {absentTests.map((t, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: "11px",
                          background: "#ffebee",
                          color: "#c62828",
                          border: "1px solid #ef9a9a",
                          borderRadius: "4px",
                          padding: "2px 8px",
                        }}
                      >
                        Absent: {formatDate(new Date(t.dateOfExam))}
                      </span>
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ))}

      {/* ── Remarks ── */}
      <div data-pdf-section="remarks">
        <SectionTitle>Remarks</SectionTitle>
        <div
          style={{
            border: "1px solid #bdbdbd",
            borderRadius: "6px",
            minHeight: "72px",
            padding: "10px 14px",
            fontSize: "13px",
            color: remarks ? "#111" : "#9e9e9e",
            whiteSpace: "pre-wrap",
          }}
        >
          {remarks || " "}
        </div>
      </div>
    </div>
  );
});

StudentReportCard.displayName = "StudentReportCard";
export default StudentReportCard;

// ── Helpers ──

function FeesSection({ totalFees, feesPaid }) {
  const pending = totalFees - feesPaid;
  const pct = Math.min(100, Math.round((feesPaid / totalFees) * 100));
  const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

  return (
    <div
      style={{
        marginBottom: "20px",
        padding: "10px 14px",
        background: "#fafafa",
        borderRadius: "6px",
        borderLeft: "3px solid #e0e0e0",
      }}
    >
      <div style={{ fontSize: "11px", color: "#9e9e9e", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: "8px" }}>
        Fee Summary
      </div>
      <div style={{ display: "flex", gap: "28px", alignItems: "center", flexWrap: "wrap" }}>
        <FeeItem label="Total" value={fmt(totalFees)} />
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
          <FeeItem label="Paid" value={fmt(feesPaid)} />
          {/* progress bar */}
          <div style={{ flex: 1, maxWidth: "140px" }}>
            <div style={{ height: "5px", background: "#e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
              <div style={{ width: `${pct}%`, height: "100%", background: "#90a4ae", borderRadius: "4px" }} />
            </div>
          </div>
          <span style={{ fontSize: "11px", color: "#9e9e9e", whiteSpace: "nowrap" }}>{pct}%</span>
        </div>
        <FeeItem label="Pending" value={fmt(pending)} valueColor={pending > 0 ? "#b71c1c" : "#388e3c"} />
      </div>
    </div>
  );
}

function FeeItem({ label, value, valueColor = "#424242" }) {
  return (
    <div style={{ minWidth: "90px" }}>
      <div style={{ fontSize: "10px", color: "#bdbdbd", textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
      <div style={{ fontSize: "13px", color: valueColor, marginTop: "1px" }}>{value}</div>
    </div>
  );
}

function InfoCell({ label, value }) {
  return (
    <td style={{ padding: "4px 8px", width: "50%", fontSize: "13px" }}>
      <span style={{ color: "#757575", minWidth: "70px", display: "inline-block" }}>{label}:</span>
      <strong style={{ marginLeft: "4px" }}>{value}</strong>
    </td>
  );
}

function SectionTitle({ children }) {
  return (
    <div
      style={{
        fontSize: "14px",
        fontWeight: "700",
        color: "#1565c0",
        borderBottom: "1px solid #e0e0e0",
        paddingBottom: "4px",
        marginBottom: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
      }}
    >
      {children}
    </div>
  );
}

const thStyle = {
  padding: "6px 10px",
  textAlign: "left",
  fontWeight: "600",
  border: "1px solid #e0e0e0",
};

const tdStyle = {
  padding: "6px 10px",
  border: "1px solid #e0e0e0",
};
